import React, { FC, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import * as PT from 'modules/Pricing/types'

import CircleVortex from 'components/SVG/illustration/CircleVortex'

import * as T from 'types'
import * as E from 'types/enums'

import GridItemContainer from './GridItemContainer'
import PackageCardGridItem from './PackageCardGridItem'
import PackageCardsFeaturesGrid from './PackageCardsFeaturesGrid'

const PackagesContainer = styled.div`
  display: none;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 50px 40px auto 12px auto 27px auto 36px auto 37px;
    row-gap: 0;
  }
`

const BestPackageContainer = styled.div`
  display: none;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: grid;
    position: relative;
    grid-template-columns: 1fr;
    grid-template-rows: auto 12px auto 27px auto 58px auto 49px;
    row-gap: 0;

    padding: 20px 28px 24px;
    border-radius: 24px 24px 0 0;
    background-color: ${props => props.theme.colors.blue900};
    z-index: 0;
    box-shadow: 0px 24px 48px rgba(1, 70, 112, 0.32);
    height: 100%;
  }
`

interface IProps {
  pricingPackagesObj: PT.IPricingPackageObject
  featuresExclusive: PT.IFeatures
  selectable: boolean | undefined
  onClickSelect(type: E.PackageType): void
  purchasedPackage: E.PackageType | undefined
  initialized: boolean | undefined
  listingPricingPackage: T.IOrderUnpopulated | undefined
  packageInCart: E.PackageType
  selectedState: T.IState | null
}

const PackageCardsGrid: FC<IProps> = ({
  pricingPackagesObj,
  featuresExclusive,
  selectable,
  onClickSelect,
  purchasedPackage,
  initialized,
  listingPricingPackage,
  packageInCart,
  selectedState,
}) => {
  const theme = useContext(ThemeContext)

  const renderBestPackageGridItem = () => (
    <BestPackageContainer>
      <PackageCardGridItem
        columnStart={1}
        columnEnd={2}
        mode={E.PackageCardMode.Best}
        subtitle="Best for home sellers looking for end-to-end support."
        pricingPackage={pricingPackagesObj.Platinum}
        packageType={E.PackageType.platinum}
        selectable={selectable}
        selectedPackage={selectable ? (packageInCart as E.PackageType) : null}
        onClickSelect={onClickSelect}
        purchased={listingPricingPackage && purchasedPackage === E.PackageType.platinum}
        purchasedPackage={purchasedPackage}
        disabled={selectable && (!initialized || !!purchasedPackage)}
        initialized={initialized}
      />
      <CircleVortex
        style={{ position: 'absolute', top: 177, right: 0, zIndex: -1 }}
        fill={theme.colors.transparentWhiteAlpha50}
      />
    </BestPackageContainer>
  )

  return (
    <div>
      <PackagesContainer>
        <GridItemContainer
          backgroundColor={theme.colors.white}
          rowStart={2}
          rowEnd={11}
          columnStart={1}
          columnEnd={7}
          desktopBorderRadius="24px 24px 0px 0px"
        />
        <PackageCardGridItem
          columnStart={1}
          columnEnd={2}
          subtitle={pricingPackagesObj.Basic?.description || ''}
          pricingPackage={pricingPackagesObj.Basic}
          packageType={E.PackageType.basic}
          selectable={selectable}
          selectedPackage={selectable ? (packageInCart as E.PackageType) : null}
          onClickSelect={onClickSelect}
          purchased={listingPricingPackage && purchasedPackage === E.PackageType.basic}
          purchasedPackage={purchasedPackage}
          disabled={selectable && (!initialized || !!purchasedPackage)}
          initialized={initialized}
        />
        <PackageCardGridItem
          columnStart={2}
          columnEnd={3}
          subtitle={pricingPackagesObj.Premium?.description || ''}
          pricingPackage={pricingPackagesObj.Premium}
          packageType={E.PackageType.premium}
          selectable={selectable}
          selectedPackage={selectable ? (packageInCart as E.PackageType) : null}
          onClickSelect={onClickSelect}
          purchased={listingPricingPackage && purchasedPackage === E.PackageType.premium}
          purchasedPackage={purchasedPackage}
          disabled={selectable && (!initialized || !!purchasedPackage)}
          initialized={initialized}
        />
        <GridItemContainer
          rowStart={1}
          rowEnd={11}
          columnStart={3}
          columnEnd={4}
        >
          {renderBestPackageGridItem()}
        </GridItemContainer>
        <PackageCardGridItem
          columnStart={4}
          columnEnd={5}
          mode={E.PackageCardMode.ComingSoon}
          subtitle={pricingPackagesObj.Elite?.description || ''}
          pricingPackage={pricingPackagesObj.Elite}
          packageType={E.PackageType.elite}
          selectable={selectable}
          selectedPackage={selectable ? (packageInCart as E.PackageType) : null}
          onClickSelect={onClickSelect}
          purchased={listingPricingPackage && purchasedPackage === E.PackageType.elite}
          purchasedPackage={purchasedPackage}
          disabled={selectable && (!initialized || !!purchasedPackage)}
          initialized={initialized}
        />
      </PackagesContainer>
      <PackageCardsFeaturesGrid
        featuresExclusive={featuresExclusive}
        selectedState={selectedState}
      />
    </div>
  )
}

export default PackageCardsGrid
